import { useI18n } from "@uic/adapters/i18n";

import { UIIcon } from "./icon";

/**
 * @typedef {object} LabelDef
 * @property {string} key Translation key
 * @property {string} icon Raw SVG string
 */

/**
 * Renders a domain concept label in one of three modes:
 * - "icon": icon only with tooltip
 * - "label": icon + text, neutral color
 * - "galaxy": icon + text, primary color (Mario Galaxy style)
 * @param {object} props Component props
 * @param {LabelDef} props.label Label definition
 * @param {"icon" | "label" | "galaxy"} [props.mode] Display mode (default: "galaxy")
 * @param {"xs" | "sm" | "md"} [props.size] Icon size (default: "md")
 * @param {boolean} [props.inline] Use inline layout for text-baseline alignment
 * @param {string} [props.className] Additional CSS classes (overrides galaxy color)
 * @returns {import('solid-js').JSX.Element} Label element
 */
export function UILabel(props) {
  const { t } = useI18n();
  const mode = () => props.mode || "galaxy";

  const classes = () => {
    const base = props.inline
      ? "tw:inline"
      : "tw:inline-flex tw:items-center tw:gap-1";
    const extra = props.className || "";

    if (mode() === "icon") {
      return `${base} tw:d-tooltip tw:d-tooltip-right tw:d-tooltip-secondary ${extra}`;
    }
    if (mode() === "galaxy") {
      return `${base} ${extra || "tw:text-primary"}`;
    }
    return `${base} ${extra}`;
  };

  const iconClasses = () =>
    props.inline ? "tw:inline-block tw:align-text-bottom" : "";

  return (
    <span
      className={classes()}
      {...(mode() === "icon" ? { "data-tip": t(props.label.key) } : {})}
    >
      <UIIcon
        svg={props.label.icon}
        size={props.size}
        className={iconClasses()}
      />
      {mode() !== "icon" && <span>{t(props.label.key)}</span>}
    </span>
  );
}
